import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../store/app.js';
import { Money } from '../components/Money.js';
import { Icon, CATEGORY_STYLE, DEFAULT_CATEGORY_STYLE, type IconName } from '../components/Icon.js';
import { Sheet } from '../components/Sheet.js';
import { Progress } from '../components/Progress.js';
import { AreaChart, Sparkline, Donut, type DonutSlice } from '../components/Charts.js';
import { Skeleton } from '../components/Skeleton.js';
import { renderInsight, renderAssumption, t } from '../lib/i18n.js';
import { formatMoney, formatMoneyShort, formatPercent, monthName, pluralDays, dayHeading } from '../lib/format.js';
import type { Dashboard } from '../lib/api.js';

export function Home() {
  const { dashboard, loadingDashboard, user, accounts } = useApp();

  return (
    <>
      <Header name={user?.name ?? null} alert={dashboard?.insight?.severity === 'critical'} />

      <main className="screen stack">
        {!dashboard && loadingDashboard && <LoadingHome />}
        {dashboard?.empty && <EmptyStart hasAccounts={accounts.length > 0} />}

        {dashboard && !dashboard.empty && (
          <div className="desktop-grid">
            <div className="stack">
              <HeroBalance data={dashboard} />
              <SafeToSpendCard data={dashboard} />
              <StatTiles data={dashboard} />
              <ExpenseOverview data={dashboard} />
              <RecentTransactions data={dashboard} />
            </div>
            <div className="stack">
              <UpcomingCard data={dashboard} />
              <InsightCard data={dashboard} />
              {dashboard.goals.length > 0 && <GoalsCard data={dashboard} />}
            </div>
          </div>
        )}
      </main>
    </>
  );
}

/* ---------------------------------------------------------------- header */

function Header({ name, alert }: { name: string | null; alert: boolean }) {
  return (
    <header className="app-header">
      <div className="app-header__bar">
        <Link to="/insights" className="header-btn" aria-label="התראות">
          <Icon name="bell" size={21} />
          {alert && <span className="header-btn__dot" />}
        </Link>
        <div>
          <div className="app-header__title">שלום, {name ?? 'שם'} 👋</div>
        </div>
        <Link to="/profile" className="header-btn" aria-label="תפריט">
          <Icon name="menu" size={22} />
        </Link>
      </div>
      <p className="app-header__sub">הנה סקירה של המצב הפיננסי שלך</p>
    </header>
  );
}

/* ------------------------------------------------------------------ hero */

function HeroBalance({ data }: { data: Dashboard }) {
  const { preferences, setPreferences } = useApp();
  const hidden = preferences?.hideAmounts ?? false;
  const month = monthName(data.month.period.start);

  // The delta belongs to the number above it: how the balance moved since the
  // month began — not a comparison of two months' cash flow.
  const opening = data.charts.balance[0]?.value ?? null;
  const closing = data.charts.balance.at(-1)?.value ?? null;
  const ratio =
    opening !== null && closing !== null && opening !== 0 ? (closing - opening) / Math.abs(opening) : null;

  return (
    <section className="hero" aria-label="יתרה כוללת">
      <div className="hero__top">
        <span className="hero__label">
          <button
            className="inline-target"
            style={{ color: 'inherit', display: 'grid', placeItems: 'center' }}
            onClick={() => void setPreferences({ hideAmounts: !hidden })}
            aria-label={hidden ? 'הצג סכומים' : 'הסתר סכומים'}
            aria-pressed={hidden}
          >
            <Icon name={hidden ? 'eye-off' : 'eye'} size={16} />
          </button>
          יתרה כוללת
        </span>
        <span className="hero__chip">
          <Icon name="chevron-down" size={14} />
          החודש
        </span>
      </div>

      <div className="hero__amount money">
        <Money value={data.position.netWorth} size="inherit" tone="inherit" />
      </div>

      <div className="hero__delta">
        {ratio !== null ? (
          <>
            <span style={{ color: ratio >= 0 ? 'var(--positive)' : 'var(--negative)', fontWeight: 600 }}>
              <Icon name={ratio >= 0 ? 'arrow-up' : 'arrow-down'} size={13} /> {formatPercent(Math.abs(ratio), 1)}
            </span>
            מתחילת {month}
          </>
        ) : (
          <span>אין עדיין נתונים להשוואה</span>
        )}
      </div>

      <div className="hero__chart">
        <AreaChart
          points={data.charts.balance}
          monthLabel={month}
          ariaLabel={`מהלך היתרה בחודש ${month}`}
          height={104}
        />
      </div>
    </section>
  );
}

/* --------------------------------------------------- safe to spend (§10) */

function SafeToSpendCard({ data }: { data: Dashboard }) {
  const [open, setOpen] = useState(false);
  const s = data.safeToSpend;
  const negative = s.amount < 0;

  return (
    <>
      <button className="card" style={{ textAlign: 'start', width: '100%' }} onClick={() => setOpen(true)}>
        <div className="between">
          <span className="row" style={{ gap: 8 }}>
            <span className="stat__badge" style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}>
              <Icon name="shield" size={16} />
            </span>
            <span>
              <span style={{ display: 'block', fontSize: 13, color: 'var(--text-2)', fontWeight: 500 }}>
                {t.home.safeToSpend}
              </span>
              <span style={{ display: 'block', fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>
                <Money value={s.amount} size="inherit" tone={negative ? 'negative' : 'neutral'} />
              </span>
            </span>
          </span>
          <span className="badge" data-tone={s.confidence === 'high' ? 'positive' : s.confidence === 'low' ? 'warning' : undefined}>
            {t.confidence[s.confidence]}
          </span>
        </div>
        <p className="label" style={{ marginTop: 10 }}>
          {negative
            ? 'החיובים הצפויים גבוהים מהיתרה הזמינה'
            : `${formatMoney(s.perDay)} ליום · ${pluralDays(s.daysUntilNextIncome)} עד ההכנסה הבאה`}
        </p>
      </button>

      <Sheet open={open} title={t.home.safeToSpend} onClose={() => setOpen(false)}>
        <div className="stack">
          <div className="card" style={{ background: 'var(--surface-2)', boxShadow: 'none' }}>
            <BreakdownRow label="יתרה זמינה" value={s.breakdown.availableLiquidCash} />
            <BreakdownRow label="חיובים צפויים" value={s.breakdown.upcomingObligations} />
            <BreakdownRow label="חיסכון מתוכנן" value={s.breakdown.reservedSavings} />
            <BreakdownRow label="תשלומי חוב" value={s.breakdown.requiredDebtPayments} />
            <BreakdownRow label="כרית ביטחון" value={s.breakdown.safetyBuffer} />
            <div style={{ height: 1, background: 'var(--hairline)', margin: '10px 0' }} />
            <div className="between">
              <strong>פנוי להוצאה</strong>
              <strong><Money value={s.amount} tone={negative ? 'negative' : 'neutral'} /></strong>
            </div>
          </div>
          <div className="stack" style={{ gap: 6 }}>
            <span className="label" style={{ fontWeight: 600 }}>על מה זה מבוסס</span>
            {s.assumptions.map((a) => (
              <p key={a.key} className="label">• {renderAssumption(a)}</p>
            ))}
          </div>
        </div>
      </Sheet>
    </>
  );
}

function BreakdownRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="between" style={{ padding: '5px 0' }}>
      <span className="label">{label}</span>
      <Money value={value} tone={value < 0 ? 'muted' : 'neutral'} />
    </div>
  );
}

/* ------------------------------------------------------------ stat tiles */

function StatTiles({ data }: { data: Dashboard }) {
  const tiles = [
    {
      label: t.home.income,
      value: data.month.income,
      delta: data.comparison.income.ratio,
      good: (r: number) => r >= 0,
      icon: 'wallet' as IconName,
      color: 'var(--c-green)',
      soft: 'var(--c-green-soft)',
      series: data.charts.income,
    },
    {
      label: t.home.expenses,
      value: data.month.expenses,
      delta: data.comparison.expenses.ratio,
      good: (r: number) => r <= 0,
      icon: 'arrow-down' as IconName,
      color: 'var(--c-red)',
      soft: 'var(--c-red-soft)',
      series: data.charts.expenses,
    },
    {
      label: 'יתרה נטו',
      value: data.month.net,
      delta: data.comparison.net.ratio,
      good: (r: number) => r >= 0,
      icon: 'pie' as IconName,
      color: 'var(--c-blue)',
      soft: 'var(--c-blue-soft)',
      series: data.charts.balance,
    },
  ];

  return (
    <div className="stat-grid">
      {tiles.map((tile) => (
        <section className="stat" key={tile.label}>
          <div className="stat__top">
            <span className="stat__label">{tile.label}</span>
            <span className="stat__badge" style={{ background: tile.soft, color: tile.color }}>
              <Icon name={tile.icon} size={15} />
            </span>
          </div>
          <div className="stat__value money"><Money value={tile.value} size="inherit" tone="inherit" /></div>
          {tile.delta !== null ? (
            <div
              className="stat__delta"
              style={{ color: tile.good(tile.delta) ? 'var(--positive)' : 'var(--negative)' }}
            >
              <Icon name={tile.delta >= 0 ? 'arrow-up' : 'arrow-down'} size={12} />
              {formatPercent(Math.abs(tile.delta), 1)}
            </div>
          ) : (
            <div className="stat__delta" style={{ color: 'var(--text-3)' }}>—</div>
          )}
          <div className="stat__spark">
            <Sparkline points={tile.series} color={tile.color} />
          </div>
        </section>
      ))}
    </div>
  );
}

/* ------------------------------------------------------- expense overview */

const DONUT_COLORS = [
  'var(--c-blue)', 'var(--c-green)', 'var(--c-red)', 'var(--c-purple)',
  'var(--c-violet)', 'var(--c-pink)', 'var(--c-grey)',
];

function ExpenseOverview({ data }: { data: Dashboard }) {
  const { categoryName, categorySlug } = useApp();
  const rows = data.categories.rows;
  const other = data.categories.other;

  if (data.categories.total === 0) {
    return (
      <section className="card">
        <div className="card-head"><h2>סקירת הוצאות</h2></div>
        <p className="label">אין עדיין הוצאות החודש.</p>
      </section>
    );
  }

  const slices: DonutSlice[] = [
    ...rows.map((row, i) => ({
      id: row.categoryId ?? `row-${i}`,
      value: row.amount,
      color: DONUT_COLORS[i % DONUT_COLORS.length]!,
      label: categoryName(row.categoryId),
    })),
    ...(other ? [{ id: 'other', value: other.amount, color: 'var(--c-grey)', label: 'אחר' }] : []),
  ];

  return (
    <section className="card" aria-label="סקירת הוצאות">
      <div className="card-head">
        <h2>סקירת הוצאות</h2>
        <Link to="/insights" className="card-link inline-target">{t.home.viewAll}</Link>
      </div>
      <div className="donut-wrap">
        <Donut
          slices={slices}
          size={126}
          thickness={22}
          centerTop={formatMoneyShort(data.categories.total)}
          centerBottom="סה״כ הוצאות"
        />
        <div className="legend">
          {slices.map((slice, i) => {
            const style = i < rows.length
              ? CATEGORY_STYLE[categorySlug(rows[i]!.categoryId)] ?? DEFAULT_CATEGORY_STYLE
              : DEFAULT_CATEGORY_STYLE;
            const share = data.categories.total === 0 ? 0 : slice.value / data.categories.total;
            return (
              <div className="legend__row" key={slice.id}>
                <span className="legend__dot" style={{ color: slice.color }}>
                  <Icon name={style.icon} size={15} />
                </span>
                <span className="legend__name">{slice.label}</span>
                <span className="legend__share">{formatPercent(share)}</span>
                <span className="legend__amount money"><Money value={slice.value} size="inherit" tone="inherit" /></span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------- recent transactions */

function RecentTransactions({ data }: { data: Dashboard }) {
  const { categoryName, categorySlug } = useApp();

  return (
    <section className="card" aria-label="עסקאות אחרונות">
      <div className="card-head">
        <h2>עסקאות אחרונות</h2>
        <Link to="/transactions" className="card-link inline-target">{t.home.viewAll}</Link>
      </div>
      {data.recent.length === 0 ? (
        <p className="label">עדיין אין תנועות.</p>
      ) : (
        <div className="list">
          {data.recent.map((tx) => {
            const style = CATEGORY_STYLE[categorySlug(tx.categoryId)] ?? DEFAULT_CATEGORY_STYLE;
            return (
              <div className="tx-row" key={tx.id}>
                <span className="tx-row__icon" style={{ background: style.soft, color: style.color }}>
                  <Icon name={style.icon} size={19} />
                </span>
                <span className="tx-row__body">
                  <span className="tx-row__name">{tx.merchant ?? t.types[tx.type] ?? tx.type}</span>
                  <span className="tx-row__meta">{tx.type === 'transfer' ? 'העברה' : categoryName(tx.categoryId)}</span>
                </span>
                <span className="tx-row__side">
                  <span className={`tx-row__amount money ${tx.amount > 0 ? 'money--positive' : 'money--negative'}`}>
                    <Money value={tx.amount} size="inherit" tone="inherit" />
                  </span>
                  <span className="tx-row__date">{dayHeading(tx.occurredAt, data.today)}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------- upcoming */

function UpcomingCard({ data }: { data: Dashboard }) {
  const { categorySlug } = useApp();
  const items = data.upcoming.next7Days.items;

  return (
    <section className="card" aria-label={t.home.upcoming}>
      <div className="card-head">
        <h2>{t.home.upcoming}</h2>
        <Link to="/budget" className="card-link inline-target">{t.home.viewAll}</Link>
      </div>
      {items.length === 0 ? (
        <p className="label">אין חיובים צפויים בשבוע הקרוב.</p>
      ) : (
        <>
          <div className="list">
            {items.slice(0, 4).map((item) => {
              const style = CATEGORY_STYLE[categorySlug(item.categoryId)] ?? DEFAULT_CATEGORY_STYLE;
              return (
                <div className="tx-row" key={item.id}>
                  <span className="tx-row__icon" style={{ background: style.soft, color: style.color }}>
                    <Icon name={style.icon} size={18} />
                  </span>
                  <span className="tx-row__body">
                    <span className="tx-row__name">{item.name}</span>
                    <span className="tx-row__meta">{dayHeading(item.dueDate, data.today)}</span>
                  </span>
                  <span className={`tx-row__amount money ${item.amount > 0 ? 'money--positive' : ''}`}>
                    <Money value={item.amount} size="inherit" tone="inherit" />
                  </span>
                </div>
              );
            })}
          </div>
          <p className="label" style={{ marginTop: 10 }}>
            {formatMoney(data.upcoming.next7Days.total)} צפויים לרדת ב־7 הימים הקרובים
          </p>
        </>
      )}
    </section>
  );
}

/* --------------------------------------------------------------- insight */

function InsightCard({ data }: { data: Dashboard }) {
  const { categoryName } = useApp();
  if (!data.insight) return null;
  const insight = data.insight;
  const tone = insight.severity === 'critical' ? 'danger'
    : insight.severity === 'warning' ? 'warning'
    : insight.severity === 'positive' ? 'positive' : 'accent';

  return (
    <section className="card" aria-label="תובנה">
      <div className="card-head">
        <span className="badge" data-tone={tone}>
          <Icon name={insight.severity === 'positive' || insight.severity === 'info' ? 'sparkle' : 'warning'} size={13} />
          תובנה
        </span>
        {data.insightCount > 1 && (
          <Link to="/insights" className="card-link inline-target">עוד {data.insightCount - 1}</Link>
        )}
      </div>
      <p style={{ fontSize: 15, lineHeight: 1.55 }}>{renderInsight(insight, { categoryName })}</p>
    </section>
  );
}

/* ----------------------------------------------------------------- goals */

function GoalsCard({ data }: { data: Dashboard }) {
  return (
    <section className="card" aria-label={t.home.goals}>
      <div className="card-head">
        <h2>{t.home.goals}</h2>
        <Link to="/goals" className="card-link inline-target">{t.home.viewAll}</Link>
      </div>
      <div className="stack">
        {data.goals.map((goal) => (
          <div key={goal.id} className="stack" style={{ gap: 6 }}>
            <div className="between">
              <span style={{ fontWeight: 600, fontSize: 14 }}>{goal.name}</span>
              <span className="label--tiny">{Math.round(goal.progress * 100)}%</span>
            </div>
            <Progress value={goal.progress} tone={goal.onTrack === false ? 'warning' : 'positive'} label={goal.name} />
            <div className="between">
              <span className="label--tiny money">
                <Money value={goal.current} size="inherit" tone="inherit" /> מתוך <Money value={goal.target} size="inherit" tone="inherit" />
              </span>
              {goal.requiredMonthly !== null && (
                <span className="label--tiny">{formatMoney(goal.requiredMonthly)} לחודש</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- states */

function LoadingHome() {
  return (
    <div className="stack">
      <Skeleton height={210} radius={20} />
      <Skeleton height={92} radius={16} />
      <div className="stat-grid">
        <Skeleton height={126} radius={16} />
        <Skeleton height={126} radius={16} />
        <Skeleton height={126} radius={16} />
      </div>
      <Skeleton height={210} radius={16} />
      <span className="sr-only">טוען…</span>
    </div>
  );
}

function EmptyStart({ hasAccounts }: { hasAccounts: boolean }) {
  return (
    <section className="card empty">
      <span className="stat__badge" style={{ width: 48, height: 48, background: 'var(--primary-soft)', color: 'var(--primary)' }}>
        <Icon name="sparkle" size={22} />
      </span>
      <h2 className="empty__title">בוא נתחיל</h2>
      <p className="empty__body">
        {hasAccounts
          ? 'הוסף הוצאה ראשונה כדי להתחיל להבין לאן הכסף הולך.'
          : 'הוסף את החשבון הראשון שלך, ונחשב בשבילך כמה באמת פנוי לך.'}
      </p>
      <Link to="/profile" className="btn btn--primary">הוספת חשבון</Link>
    </section>
  );
}
